import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
	Box, Button, Checkbox, Chip, Collapse, FormControl,
	IconButton, ListItemText, MenuItem, Paper, Select, Tooltip, Typography,
} from '@mui/material'
import ExpandMoreIcon  from '@mui/icons-material/ExpandMore'
import FilterAltIcon   from '@mui/icons-material/FilterAlt'
import RestartAltIcon  from '@mui/icons-material/RestartAlt'

import {
	setTechFilter, clearTechFilters,
	selectFiltersForTech, selectActiveFilterCountForTech,
} from '../../reducers/filtersReducer'
import { loadFilterMatrix, selectMatrixForTech, selectMatrixLoadingForTech } from '../../reducers/lockdownReducer'
import { applyAndFetchTech } from '../../actions/kpiDataActions'

const TECH_COLORS = {
	'5G': { bg: '#e0f2f1', color: '#00695c', border: '#80cbc4' },
	'4G': { bg: '#e3f2fd', color: '#0d47a1', border: '#90caf9' },
	'3G': { bg: '#fff3e0', color: '#bf360c', border: '#ffcc80' },
	'2G': { bg: '#fce4ec', color: '#880e4f', border: '#f48fb1' },
}

const FILTER_FIELDS = [
	{ key: 'side',      label: 'Side',        rowKey: 'side'      },
	{ key: 'polygon',   label: 'Polygon',     rowKey: 'polygon'   },
	{ key: 'manager',   label: 'Manager',     rowKey: 'manager'   },
	{ key: 'optimiser', label: 'Optimiser',   rowKey: 'optimiser' },
	{ key: 'vendor',    label: 'Vendor',      rowKey: 'vendor'    },
	{ key: 'jvId',      label: 'JV_ID',       rowKey: 'jvId'      },
	{ key: 'cellName',  label: 'Cell Name',   rowKey: 'cellName'  },
]

// ── Local cascade logic ─────────────────────────────────────────────────────
// For each field X, apply all filters EXCEPT X → extract distinct values
// from surviving rows. This gives the valid options for X.
function computeCascadedOptions(matrix, techFilters) {
	const result = {}

	for (const { key, rowKey } of FILTER_FIELDS) {
		const otherFilters = FILTER_FIELDS.filter(f => f.key !== key)
		let rows = matrix

		for (const other of otherFilters) {
			const selected = techFilters[other.key]
			if (selected && selected.length > 0) {
				const set = new Set(selected)
				rows = rows.filter(r => r[other.rowKey] != null && set.has(r[other.rowKey]))
			}
		}

		const values = [...new Set(rows.map(r => r[rowKey]).filter(v => v != null))].sort()
		result[key] = values
	}

	return result
}

// Given the current filters and the new cascaded options, prune any
// selected values that no longer exist in the available options.
// Returns an object with only the keys that changed (or empty if nothing changed).
function pruneStaleSelections(techFilters, cascadedOptions) {
	const patches = {}
	for (const { key } of FILTER_FIELDS) {
		const selected  = techFilters[key] ?? []
		const available = new Set(cascadedOptions[key] ?? [])
		if (selected.length === 0) continue
		const pruned = selected.filter(v => available.has(v))
		if (pruned.length !== selected.length) {
			patches[key] = pruned
		}
	}
	return patches
}

// ── Components ──────────────────────────────────────────────────────────────

function FilterSelect({ label, value = [], options = [], onChange }) {
	const selected    = Array.isArray(value) ? value : (value ? [value] : [])
	const allSelected = options.length > 0 && selected.length === options.length

	const handleToggleAll = (e) => {
		e.stopPropagation()
		onChange(allSelected ? [] : [...options])
	}

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column' }}>
			<Typography sx={{ fontSize: 9, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em', mb: '3px' }}>
				{label}
			</Typography>
			<FormControl size="small">
				<Select
					multiple
					displayEmpty
					value={selected}
					onChange={e => onChange(e.target.value)}
					renderValue={sel => sel.length === 0
						? <span style={{ color: '#aaa', fontSize: 11 }}>(All)</span>
						: <span style={{ fontSize: 11 }}>{sel.length === 1 ? sel[0] : `${sel.length} selected`}</span>
					}
					sx={{
						height: 28, fontSize: 11, minWidth: 110,
						bgcolor: selected.length > 0 ? 'primary.light' : 'background.default',
						'& .MuiOutlinedInput-notchedOutline': {
							borderColor: selected.length > 0 ? 'primary.main' : 'divider'
						},
					}}
					MenuProps={{ PaperProps: { sx: { maxHeight: 280, minWidth: 180 } } }}
				>
					<MenuItem onMouseDown={handleToggleAll} sx={{ fontSize: 11, py: '2px', borderBottom: '1px solid', borderColor: 'divider' }}>
						<Checkbox
							checked={allSelected}
							indeterminate={selected.length > 0 && !allSelected}
							size="small" sx={{ py: 0, pr: 0.5 }}
						/>
						<ListItemText
							primary={allSelected ? 'Deselect All' : 'Select All'}
							primaryTypographyProps={{ fontSize: 11, fontWeight: 600 }}
						/>
					</MenuItem>
					{options.map(o => (
						<MenuItem key={o} value={o} sx={{ fontSize: 11, py: '2px' }}>
							<Checkbox checked={selected.includes(o)} size="small" sx={{ py: 0, pr: 0.5 }} />
							<ListItemText primary={o} primaryTypographyProps={{ fontSize: 11 }} />
						</MenuItem>
					))}
				</Select>
			</FormControl>
		</Box>
	)
}

function FilterSummaryChip({ label, count, total }) {
	const hasFilter = count > 0
	return (
		<Chip
			label={
				<Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: '4px' }}>
					<Typography component="span" sx={{ fontSize: 9, fontWeight: 700, color: hasFilter ? 'primary.main' : 'text.disabled', letterSpacing: '0.04em' }}>
						{label}
					</Typography>
					<Typography component="span" sx={{ fontSize: 10, fontFamily: 'IBM Plex Mono, monospace', color: hasFilter ? 'primary.dark' : 'text.disabled' }}>
						{hasFilter ? `(${count}/${total})` : '(All)'}
					</Typography>
				</Box>
			}
			size="small"
			sx={{
				height: 20,
				bgcolor: hasFilter ? 'primary.light' : 'transparent',
				border: '1px solid',
				borderColor: hasFilter ? 'primary.main' : 'divider',
				borderRadius: 1,
				'& .MuiChip-label': { px: '6px' },
			}}
		/>
	)
}

// ── Main component ──────────────────────────────────────────────────────────

export default function LockdownKPIDashboardTechFilters({ tech }) {
	const dispatch      = useDispatch()
	const lockdownId    = useSelector(s => s.lockdown.selected?.lockdownId)
	const dateFrom      = useSelector(s => s.filters.dateFrom)
	const dateTo        = useSelector(s => s.filters.dateTo)
	const techFilters   = useSelector(selectFiltersForTech(tech))
	const matrix        = useSelector(selectMatrixForTech(tech))
	const matrixLoading = useSelector(selectMatrixLoadingForTech(tech))
	const activeCount   = useSelector(selectActiveFilterCountForTech(tech))
	const chartLoading  = useSelector(s => s.kpiData.loadingKeys.some(k => k.startsWith(`${tech}::`)))

	const [expanded, setExpanded] = useState(false)
	const techStyle               = TECH_COLORS[tech] ?? TECH_COLORS['4G']

	// Load matrix once when lockdown, tech, or dates change
	useEffect(() => {
		if (lockdownId && tech) {
			dispatch(loadFilterMatrix({
				id: lockdownId,
				tech,
				dateFrom: dateFrom || null,
				dateTo:   dateTo   || null,
			}))
		}
	}, [dispatch, lockdownId, tech, dateFrom, dateTo])

	// Cascade locally — recomputed instantly on every filter change
	const cascadedOptions = useMemo(
		() => computeCascadedOptions(matrix, techFilters),
		[matrix, techFilters]
	)

	// Full options (no filters) — used for summary chip totals
	const fullOptions = useMemo(
		() => computeCascadedOptions(matrix, {
			jvId: [], cellName: [], side: [], polygon: [],
			vendor: [], manager: [], optimiser: [],
		}),
		[matrix]
	)

	// Auto-prune stale selections when cascade changes options
	useEffect(() => {
		if (matrix.length === 0) return
		const patches = pruneStaleSelections(techFilters, cascadedOptions)
		for (const [key, value] of Object.entries(patches)) {
			dispatch(setTechFilter({ tech, key, value }))
		}
	}, [cascadedOptions]) // eslint-disable-line react-hooks/exhaustive-deps

	const handleChange = (key) => (value) => {
		// 1. Set the filter the user just changed
		dispatch(setTechFilter({ tech, key, value }))

		// 2. Compute what the new cascade looks like with this change
		const newFilters = { ...techFilters, [key]: value }
		const newOptions = computeCascadedOptions(matrix, newFilters)

		// 3. Prune other filters that now have stale values
		const patches = pruneStaleSelections(newFilters, newOptions)
		for (const [patchKey, patchValue] of Object.entries(patches)) {
			dispatch(setTechFilter({ tech, key: patchKey, value: patchValue }))
		}
	}

	const handleClear = () => {
		dispatch(clearTechFilters({ tech }))
	}

	const handleApply = () => dispatch(applyAndFetchTech(tech))

	return (
		<Paper
			elevation={0}
			sx={{
				borderRadius: 3,
				border: '1px solid',
				borderColor: 'divider',
				borderTopWidth: 3,
				borderTopColor: techStyle.color,
				bgcolor: 'background.paper',
				flexShrink: 0,
				overflow: 'hidden',
			}}
		>
			{/* Header — always visible */}
			<Box sx={{
				display: 'flex', alignItems: 'center', gap: 1.25,
				px: 1.25, py: '6px',
				cursor: 'pointer',
				'&:hover': { bgcolor: 'action.hover' },
			}}
				onClick={() => setExpanded(v => !v)}>

				<Chip label={tech} size="small" sx={{
					bgcolor: techStyle.bg, color: techStyle.color,
					border: `1px solid ${techStyle.border}`,
					fontWeight: 700, fontSize: 10, height: 20, borderRadius: 1, flexShrink: 0,
				}} />

				{!expanded && (
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
						{FILTER_FIELDS.map(({ key, label }) => (
							<FilterSummaryChip
								key={key}
								label={label}
								count={(techFilters[key] ?? []).length}
								total={(fullOptions[key] ?? []).length}
							/>
						))}
					</Box>
				)}

				{expanded && (
					<Typography sx={{ fontSize: 10, color: 'text.secondary', fontWeight: 600, flex: 1 }}>
						Filters · {activeCount > 0 ? `${activeCount} active` : 'no filters applied'}
						{matrixLoading && ' · loading…'}
					</Typography>
				)}

				{activeCount > 0 && (
					<Tooltip title="Clear all filters for this tech">
						<IconButton
							size="small"
							onClick={(e) => { e.stopPropagation(); handleClear() }}
							sx={{ p: '2px', color: 'text.secondary', '&:hover': { color: 'error.main' } }}
						>
							<RestartAltIcon sx={{ fontSize: 14 }} />
						</IconButton>
					</Tooltip>
				)}

				<IconButton size="small" sx={{ p: '2px' }}>
					<ExpandMoreIcon sx={{
						fontSize: 16,
						transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
						transition: 'transform .2s',
					}} />
				</IconButton>
			</Box>

			{/* Expanded */}
			<Collapse in={expanded} timeout="auto" unmountOnExit>
				<Box sx={{
					display: 'flex', alignItems: 'flex-end', gap: 1, flexWrap: 'wrap',
					px: 1.25, pb: '8px', pt: '4px',
					borderTop: '1px solid', borderColor: 'divider',
				}}>
					{FILTER_FIELDS.map(({ key, label }) => (
						<FilterSelect
							key={key}
							label={label}
							value={techFilters[key]}
							options={cascadedOptions[key] ?? []}
							onChange={handleChange(key)}
						/>
					))}

					<Box sx={{ flex: 1 }} />

					<Button
						variant="contained" size="small"
						disabled={!lockdownId || chartLoading || matrixLoading}
						onClick={handleApply}
						sx={{ height: 28, px: 2.5, alignSelf: 'flex-end' }}
					>
						Apply
					</Button>
				</Box>
			</Collapse>
		</Paper>
	)
}